import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import Reveal from '../../components/Reveal/Reveal';
import ArrowIcon from '../../components/icons/ArrowIcon';
import HomeContactForm from '../../components/HomeContactForm/HomeContactForm';
import ContactDetails from '../../components/ContactDetails/ContactDetails';
import ContactMap from '../../components/ContactMap/ContactMap';
import { getOrganizationSchema, getLocalBusinessSchema } from '../../utils/structuredData';
import './Contact.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const HERO_BG = { '--hero-bg': `url(${PUBLIC_URL}/assets/hero-banner.png)` };

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const NEXT_STEPS = [
  {
    num: '01',
    title: 'Share a short brief',
    text: 'Tell us what you need, your timeline, and any constraints — even a rough idea is fine.',
  },
  {
    num: '02',
    title: 'We reply with next steps',
    text: 'Within about 24 hours we come back with clarifying questions and a suggested approach.',
  },
  {
    num: '03',
    title: 'Discovery and proposal',
    text: 'If it is a fit, we align on scope and share a clear proposal before any build work starts.',
  },
];

const FAQS = [
  {
    q: 'Do you sign an NDA?',
    a: 'Yes. We can sign an NDA before a detailed discussion so your idea and business information stay confidential.',
  },
  {
    q: 'Who owns the source code?',
    a: 'You own the source code and documentation for what we deliver on your project, as agreed in the engagement.',
  },
  {
    q: 'How long does a typical project take?',
    a: 'It depends on scope. A focused MVP can take weeks to a few months; larger platforms take longer. After discovery we share a realistic timeline.',
  },
  {
    q: 'Do you provide support after launch?',
    a: 'Yes. We can continue with fixes, improvements, monitoring, and planned feature work under a support or dedicated-team arrangement.',
  },
];

const Contact = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://www.codeinventivsolutions.com';

  return (
    <div className="contact-page">
      <SEO
        title="Contact Us | Request a Quote | CodeInventiv Solutions"
        description="Contact CodeInventiv Solutions in Jaipur for custom software, web apps, and mobile development. Share a brief and we will reply within 24 hours with next steps."
        keywords="contact CodeInventiv Solutions, software development quote Jaipur, custom software inquiry, web app development contact"
        structuredData={[
          getOrganizationSchema(siteUrl),
          getLocalBusinessSchema(siteUrl, 'india'),
        ]}
        ogType="website"
      />

      <section className="page-hero" style={HERO_BG}>
        <div className="container page-hero-inner">
          <Reveal>
            <div className="section-eyebrow on-dark">Contact</div>
            <h1 className="page-hero-title">
              Tell us about your project.<br />
              <em>We&apos;ll take it from there.</em>
            </h1>
            <p className="page-hero-sub">
              Share a short brief by form, email, phone, or WhatsApp. We respond within 24 hours with
              questions and a practical next step — at no cost.
            </p>
            <div className="page-hero-points">
              <span><CheckIcon /> NDA available before detailed discussion</span>
              <span><CheckIcon /> Clear proposal after discovery</span>
              <span><CheckIcon /> Based in Jaipur, India</span>
            </div>
            <div className="page-hero-actions">
              <a href="#contact-form" className="btn btn-gold">
                Send a Brief
                <ArrowIcon />
              </a>
              <Link to="/services" className="btn btn-ghost-light">View Services</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section section-pale" id="next-steps">
        <div className="container">
          <Reveal className="section-head">
            <div className="section-eyebrow">What Happens Next</div>
            <h2 className="section-title">A simple path from <em>inquiry to proposal</em></h2>
          </Reveal>
          <div className="contact-steps-grid">
            {NEXT_STEPS.map((step) => (
              <Reveal key={step.num} as="article" className="contact-step-card">
                <div className="contact-step-num">{step.num}</div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-light" id="contact-form">
        <div className="container">
          <div className="contact-wrap">
            <Reveal className="contact-info">
              <div className="section-eyebrow">Get In Touch</div>
              <h2 className="section-title">Reach us by form, email, <em>or WhatsApp</em></h2>
              <p className="section-sub" style={{ marginBottom: '2.2rem' }}>
                Our India office is in Jaipur. Dubai contact details are listed for regional
                inquiries.
              </p>
              <ContactDetails />
            </Reveal>
            <HomeContactForm />
          </div>
        </div>
      </section>

      <ContactMap />

      <section className="section section-pale" id="faq">
        <div className="container contact-faq-wrap">
          <Reveal className="section-head">
            <div className="section-eyebrow">FAQ</div>
            <h2 className="section-title">Before you <em>reach out</em></h2>
          </Reveal>
          <div className="contact-faq-list">
            {FAQS.map((item, i) => {
              const open = openFaq === i;
              return (
                <Reveal key={item.q} className={`contact-faq-item${open ? ' open' : ''}`}>
                  <button
                    type="button"
                    className="contact-faq-question"
                    aria-expanded={open}
                    onClick={() => setOpenFaq(open ? -1 : i)}
                  >
                    {item.q}
                    <span aria-hidden="true">{open ? '−' : '+'}</span>
                  </button>
                  {open && <p className="contact-faq-answer">{item.a}</p>}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
